import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  sendPasswordResetEmail,
  UserCredential
} from "firebase/auth";
import { auth } from "../config/firebase";
// @ts-ignore
import { reloadSentryConfigAfterAuth, clearSentryUser } from "../utils/sentry";
import { UserSessionRegistry } from "../stores/user-session.registry";
import { onAuthFailure, offAuthFailure } from "../services/authEvents";
import { isPublicRoute, skipsAuthGateway } from "../utils/publicRoutes";
import { recordAuthEvent, refreshAuthJournalSwitch } from "../utils/authJournal";
import { rememberReturnPath, forgetReturnPath } from "../utils/loginReturn";

// Marker written before signInWithRedirect navigates away, read on the next page
// load. While it is set, AuthContext keeps `loading` true until getRedirectResult
// settles — otherwise onAuthStateChanged(null) fires first and the login form
// flashes over a sign-in that is actually still in flight.
const REDIRECT_SIGNIN_FLAG = 'firebase_redirect_login';

export const markRedirectSignInPending = () => {
  try {
    sessionStorage.setItem(REDIRECT_SIGNIN_FLAG, 'true');
  } catch (e) {
    console.warn('[AuthContext] Could not persist redirect sign-in flag:', e);
  }
};

export const clearRedirectSignInPending = () => {
  try {
    sessionStorage.removeItem(REDIRECT_SIGNIN_FLAG);
  } catch (e) {
    console.warn('[AuthContext] Could not clear redirect sign-in flag:', e);
  }
};

export const isRedirectSignInPending = (): boolean => {
  try {
    return sessionStorage.getItem(REDIRECT_SIGNIN_FLAG) === 'true';
  } catch (e) {
    return false;
  }
};

// REMOVED: orgValidationCache - No longer needed after removing unnecessary organization validation
// The backend already validates organizations before returning them, so individual validation
// was causing unnecessary HTTP requests (5+ per user login).

// Declarar el tipo para importMeta
declare global {
  interface ImportMeta {
    env: Record<string, string>;
  }
}

interface PlanPrice {
  key: string;
  amount: number;
}

interface PlanPriceTier {
  yearly: PlanPrice;
  monthly: PlanPrice;
}

interface PlanPricing {
  [key: string]: PlanPriceTier; // "1000", "5000", "10000"
}

interface PlanPrices {
  USD: PlanPricing;
}

interface PlanFeatures {
  en: {
    general: string[];
  };
  es: {
    general: string[];
  };
}

interface PlanName {
  en: string;
  es: string;
}

interface Plan {
  id: number;
  label: string;
  name: PlanName;
  description: PlanName;
  price: PlanPrices;
  features: PlanFeatures;
  tiers: string[];
  currencies: string[];
  featured: boolean;
  public: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Subscription {
  id: number;
  organizationId: number;
  userId: number;
  planId: number;
  paymentToken: string;
  paymentMethod: string;
  status: string;
  invoices: null;
  features: null;
  startedAt: string;
  createdAt: string;
  updatedAt: string;
  plan: Plan;
}

interface Organization {
  id: number;
  name: string;
  description: string;
  slug: string;
  hubspotUid: string | null;
  type: string;
  industry: string;
  targetMarket: string;
  createdAt: string;
  updatedAt: string;
  subscriptions: Subscription[];
}

interface Member {
  id: number;
  organizationId: number;
  userId: number;
  role: string;
  createdAt: string;
  updatedAt: string;
  organization: Organization;
}

interface Module {
  id: number;
  code: string;
  name: string;
  description?: string;
  is_basic?: boolean;
}

interface UserInfo {
  user: {
    id: number;
    uid: string;
    legalId: string | null;
    email: string;
    name: string | null;
    phone: string | null;
    state: string | null;
    city: string | null;
    address: string | null;
    firebaseUid: string;
    hubspotUid: string;
    age: number | null;
    profileData: any;
    createdFromContext: null;
    createdAt: string;
    updatedAt: string;
    role?: string;
    members: Member[];
    modules?: Module[]; // Módulos asignados al usuario
    bt_id?: number; // ID de BT4SAP (en minúsculas para consistencia con el backend)
    extra_data?: any; // Datos adicionales en formato JSONB
  };
}

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<UserCredential>;
  signup: (email: string, password: string) => Promise<UserCredential>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<UserCredential>;
  signInWithGoogleRedirect: () => Promise<void>;
  cancelRedirectSignIn: () => void;
  loading: boolean;
  userInfo: UserInfo | null;
  role: string | null;
  userOrganizations: any[]; // Lista de organizaciones del usuario
  activeOrganization: any | null; // Organización actualmente seleccionada
  setActiveOrganization: (org: any) => void; // Función para cambiar la organización activa
  forceSyncWithBackend: () => Promise<boolean>;
  startImpersonation: (targetEmail: string, reason?: string) => Promise<boolean>;
  endImpersonation: () => Promise<boolean>;
  isImpersonating: boolean;
  impersonatedEmail: string | null;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  return useContext(AuthContext);
};

// @ts-ignore
import UserService from '../services/user.service';
import { loadOrgsWithGuard } from '../utils/orgLoadGuard';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  
  // Initialize role and activeOrganization from localStorage immediately
  // This ensures they're available even before organizations are loaded
  const [role, setRole] = useState<string | null>(() => {
    try {
      const authData = localStorage.getItem('authData');
      if (authData) {
        const parsed = JSON.parse(authData);
        const role = parsed.role_code || null;
        console.log(`🔍 [AuthContext] Rol inicial desde localStorage: ${role}`);
        return role;
      }
    } catch (e) {
      console.warn('Error reading role from localStorage:', e);
    }
    console.log(`🔍 [AuthContext] No hay rol en localStorage, inicializando como null`);
    return null;
  });
  
  const [activeOrganization, setLocalActiveOrganization] = useState<any>(() => {
    try {
      const savedOrg = localStorage.getItem('activeOrganization');
      if (savedOrg) {
        return JSON.parse(savedOrg);
      }
    } catch (e) {
      console.warn('Error reading activeOrganization from localStorage:', e);
    }
    return null;
  });
  
  // Initialize userOrganizations from localStorage if available (for faster initial render)
  // This ensures the selector shows all organizations immediately after page reload
  const [userOrganizations, setUserOrganizations] = useState<any[]>(() => {
    try {
      const saved = localStorage.getItem('userOrganizations');
      if (saved) {
        const parsed = JSON.parse(saved);
        console.log(`[AuthContext] Initialized userOrganizations from localStorage: ${parsed.length} organizations`);
        return parsed;
      }
    } catch (e) {
      console.warn('[AuthContext] Error reading userOrganizations from localStorage:', e);
    }
    return [];
  });
  const LOGOUT_EVENT_KEY = 'auth:logout_event';

  const buildLoginRedirectPath = (reason: string) => {
    switch (reason) {
      case 'auto-logout':
        return '/firebase-login?auto-logout=true';
      case 'session-expired':
        return '/firebase-login?session-expired=true';
      case 'access-denied':
        return '/firebase-login?access-denied=true';
      case 'account-blocked':
        return '/firebase-login?account-blocked=true';
      case 'org-suspended':
        return '/firebase-login?org-suspended=true';
      case 'manual-logout':
      default:
        return '/firebase-login?manual-logout=true';
    }
  };

  const broadcastLogoutEvent = (reason: string) => {
    try {
      localStorage.setItem(
        LOGOUT_EVENT_KEY,
        JSON.stringify({ reason, ts: Date.now() })
      );
    } catch (e) {
      console.warn('[AuthContext] Could not write logout event:', e);
    }
  };

  const isMobileDevice = (): boolean => {
    const ua = navigator.userAgent;
    const isTouchMac = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
    return /iPhone|iPad|iPod|Android|Mobile/i.test(ua) || isTouchMac;
  };

  const redirectToLogin = (reason: string, reload = false) => {
    recordAuthEvent('redirectToLogin', () => ({
      reason,
      user: !!auth.currentUser,
      detail: { reload, publicRoute: isPublicRoute() },
    }));
    // Backstop for every redirect in this file. A page that renders without a
    // session must never be navigated away from because a session could not be
    // established — that is not a failure there, it is the normal case.
    // Guarding here rather than at each call site is deliberate: the paths that
    // reach this function are many (token expiry, gateway retry, the auth
    // failure bus, cross-tab logout), and on 2026-09-04 the one that bounced a
    // customer off his own invoice was not the one anybody had guarded.
    if (isPublicRoute()) {
      console.warn(`[AuthContext] Suppressed ${reason} redirect on public route ${window.location.pathname}`);
      return;
    }
    const target = buildLoginRedirectPath(reason);
    // A logout the person asked for starts fresh; any other one brings them
    // back to the page they were on once they are signed in again.
    if (reason === 'manual-logout') forgetReturnPath();
    else rememberReturnPath();
    if (reload) {
      if (isMobileDevice()) {
        navigate(target, { replace: true });
        return;
      }
      window.history.replaceState({}, '', target);
      window.location.reload();
      return;
    }
    if (!window.location.pathname.includes('/firebase-login')) {
      window.location.href = target;
    } else {
      window.history.replaceState({}, '', target);
    }
  };

  const signup = (email: string, password: string) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const login = (email: string, password: string) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Sends Firebase's password reset email. The link opens Firebase's hosted
  // action page (served same-origin through /__/auth/*), and continueUrl
  // brings the person back to the login screen once the new password is set.
  // See backend/docs/AUTH_PASSWORD_RESET.md.
  const resetPassword = (email: string) => {
    return sendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/firebase-login`,
      handleCodeInApp: false,
    });
  };

  const logout = async () => {
    try {
      console.log("🚪 [AuthContext] Starting manual logout...");

      // Always kill impersonation first — the cookie survives normal logout otherwise.
      // Check both React state and localStorage so we catch the case where impersonation
      // was started in a previous tab or session.
      const impersonationActive =
        isImpersonating || localStorage.getItem('impersonation_active') === 'true';
      if (impersonationActive) {
        try {
          const { apiCall: _ac } = await import('../services/api.service');
          await _ac('users/end_impersonation', 'POST');
          console.log("✅ Impersonation ended before logout");
        } catch (e) {
          console.warn("[AuthContext] Could not end impersonation before logout (continuing):", e);
        }
        setIsImpersonating(false);
        setImpersonatedEmail(null);
      }

      // Clear Sentry user context before logout
      try {
        await clearSentryUser();
        console.log("✅ Sentry user context cleared");
      } catch (sentryError) {
        console.warn("Warning: Could not clear Sentry user context:", sentryError);
      }
      
      // Clear local state FIRST (before signOut to avoid race conditions)
      React.startTransition(() => {
        setCurrentUser(null);
        setUserInfo(null);
        setRole(null);
        setUserOrganizations([]);
        setLocalActiveOrganization(null);
        setLoading(false);
      });

      // Broadcast reason for cross-tab consistency BEFORE clearing auth storage
      broadcastLogoutEvent('manual-logout');
      
      // Clear localStorage BEFORE signOut
      localStorage.removeItem('authData');
      localStorage.removeItem('user');
      localStorage.removeItem('activeOrganization');
      localStorage.removeItem('userOrganizations');
      localStorage.removeItem('originalUser');
      localStorage.removeItem('impersonation_active');
      localStorage.removeItem('impersonation_target_email');
      localStorage.removeItem('userInfo');
      
      // Clear UserSessionRegistry and deactivate session key server-side
      try {
        await UserSessionRegistry.logout(auth.currentUser?.uid || undefined);
        console.log("✅ UserSessionRegistry logged out");
      } catch (e) {
        console.warn('[AuthContext] Error logging out UserSessionRegistry:', e);
      }

      // Attempt logout on server
      try {
        const { apiCall } = await import('../services/api.service');
        await apiCall('auth/logout', 'GET', null, false, {
          silentError: true,
          disableAutoLogoutOn401: true,
          shortTimeout: 10000,
        });
        console.log("✅ Logout processed on server (cookies cleared)");
      } catch (serverError: any) {
        console.warn("⚠️ Error attempting to logout on server (continuing):", serverError.message);
      }
      
      // IMPORTANT: Logout from Firebase and WAIT for it to complete
      try {
        await auth.signOut();
        console.log("✅ Firebase signOut completed successfully");
      } catch (signOutError) {
        console.warn("⚠️ Error in Firebase signOut (continuing with redirect):", signOutError);
      }
      
      // Redirect to login page
      console.log("🚪 [AuthContext] Redirecting to firebase-login...");
      redirectToLogin('manual-logout');
      
    } catch (error) {
      console.error("❌ [AuthContext] Error during logout:", error);
      // Ensure redirect even if there's an error
      redirectToLogin('manual-logout');
    }
  };

  // Provider compartido por el flujo popup y el flujo redirect — deben pedir
  // exactamente los mismos scopes para que el consentimiento sea intercambiable.
  const buildGoogleProvider = () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
    });
    provider.addScope('email');
    provider.addScope('profile');
    return provider;
  };

  // IMPORTANT: never await anything before calling this from a click handler.
  // Safari revokes the user-gesture across awaits and the popup is blocked.
  const signInWithGoogle = () => signInWithPopup(auth, buildGoogleProvider());

  // Redirect flow — used on Safari/iOS, where popups are unreliable even with a
  // same-origin auth handler. Navigates away; resolution happens in
  // getRedirectResult on the next page load.
  const signInWithGoogleRedirect = () => {
    markRedirectSignInPending();
    return signInWithRedirect(auth, buildGoogleProvider()).catch((error) => {
      clearRedirectSignInPending();
      throw error;
    });
  };

  // NUEVO: Función para forzar la sincronización con el backend
  // Pure session resync — does NOT touch organizations. Org loading used to
  // happen inside here, which meant a 401 in loadUserOrganizations() could
  // trigger forceSyncWithBackend() -> loadUserOrganizations() again (496)
  // WHILE loadUserOrganizations()'s own caller was about to retry itself too
  // -- two independent retry trees for the same failure. Callers that need
  // fresh orgs after a resync (the cross-tab handler) now call
  // loadUserOrganizations() themselves, once, after this resolves.
  const forceSyncWithBackend = async (): Promise<boolean> => {
    console.log("Forzando sincronización con el backend...");
    try {
      // Usar forceSyncSession de api.service.js que maneja toda la lógica
      const { forceSyncSession } = await import('../services/api.service');
      const syncSuccess = await forceSyncSession();

      if (!syncSuccess) {
        console.error(`Error al forzar sincronización`);
        return false;
      }

      // Obtener resultado desde localStorage después de sincronización
      const savedAuthData = localStorage.getItem('authData');
      let syncResult = null;
      if (savedAuthData) {
        try {
          syncResult = JSON.parse(savedAuthData);
        } catch (e) {
          console.warn('Error parseando authData después de sync:', e);
        }
      }

      console.log("Sincronización forzada exitosa:", syncResult);

      // Actualizar marca de sesión establecida
      localStorage.setItem('localSessionEstablished', 'true');
      localStorage.setItem('lastSyncTime', new Date().toISOString());

      if (syncResult && syncResult.email) {
        setRole(syncResult.role_code);
      }

      return true;
    } catch (error) {
      console.error("Error en sincronización forzada:", error);
      return false;
    }
  };

  // Función para definir módulos básicos según el rol
  const getDefaultModules = (_role: string): Module[] => {
    // No añadir módulos por defecto, devolver un array vacío
    return [];
  };

  // Carga las organizaciones del usuario. El dedup + retry-cap con backoff
  // real vive en utils/orgLoadGuard.ts (testeado ahí en aislamiento) —
  // 2026-10-01 incident: un useState leído dentro de la misma cadena
  // recursiva async nunca frenaba de verdad (~1,800 llamadas en una pestaña).
  // Acá solo queda la llamada HTTP real + el filtrado/logging específico de
  // este endpoint.
  const loadUserOrganizations = async (email: string): Promise<any[]> => {
    return loadOrgsWithGuard(email, {
      fallback: [],
      resync: forceSyncWithBackend,
      fetchOrgs: async (targetEmail: string) => {
        // Usar apiCall para obtener organizaciones con módulos
        const { apiCall } = await import('../services/api.service');

        const orgs = await apiCall(`users/${targetEmail}/organizations-with-modules`, 'GET', null, false, {
          forceWaitAuth: true
        });

        console.log(`[AuthContext] User organizations loaded from API:`, {
          count: Array.isArray(orgs) ? orgs.length : 0,
          organizations: orgs,
          email: targetEmail
        });

        // OPTIMIZATION: Removed unnecessary individual organization validation
        // The backend endpoint /api/users/{email}/organizations-with-modules already validates
        // and returns only valid organizations. Individual validation was causing 5+ extra HTTP requests.
        // If validation is needed, it should be done on the backend side.

        // Filter out organizations with invalid IDs (basic sanity check without HTTP calls)
        const validOrgs = Array.isArray(orgs)
          ? orgs.filter((org: any) => {
              // Basic validation: check if organization_id is valid
              if (!org.organization_id || isNaN(Number(org.organization_id)) || Number(org.organization_id) <= 0) {
                console.warn(`Organización con ID inválido: ${org.organization_id}. Omitiendo.`);
                return false;
              }
              return true;
            })
          : [];

        console.log(`[AuthContext] Organizaciones procesadas: ${validOrgs.length} de ${Array.isArray(orgs) ? orgs.length : 0}`, {
          organizations: validOrgs.map((o: any) => ({ id: o.organization_id, name: o.organization_name }))
        });

        return validOrgs;
      },
    });
  };

  // Función para cambiar la organización activa
  const handleSetActiveOrganization = async (organization: any) => {
    // Suspended orgs are inaccessible for admin/user. super_user can access all.
    if (organization.active === false && role !== 'super_user') {
      console.warn(`[AuthContext] Blocked switch to suspended org ${organization.organization_id}`);
      return;
    }

    const previousOrg = activeOrganization;
    const previousRole = role;
    try {
      // Switch org display immediately for UX — but do NOT change role yet.
      setLocalActiveOrganization(organization);
      localStorage.setItem('activeOrganization', JSON.stringify(organization));

      // Ask the backend to confirm the role for this org context.
      const apiResult = await UserService.refreshOrganizationContext(organization.organization_id);
      const confirmedRole = apiResult?.role_code;
      if (!confirmedRole) {
        // Backend didn't return a role — revert to be safe.
        setLocalActiveOrganization(previousOrg);
        localStorage.setItem('activeOrganization', JSON.stringify(previousOrg));
        return;
      }

      // Commit role and localStorage only after backend confirmation.
      setRole(confirmedRole);
      const authData = {
        role_code: confirmedRole,
        organization_id: organization.organization_id,
        email: userInfo?.user?.email,
        user_id: userInfo?.user?.id,
        name: userInfo?.user?.name,
      };
      localStorage.setItem('authData', JSON.stringify(authData));

      if (userInfo && userInfo.user) {
        const updatedUserInfo = {
          ...userInfo,
          user: {
            ...userInfo.user,
            organization_id: organization.organization_id,
            organization_name: organization.organization_name
          }
        };
        setUserInfo(updatedUserInfo);
        localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
      }

      console.log(`Org switched to: ${organization.organization_name} (${organization.organization_id}), role: ${confirmedRole}`);
    } catch (error) {
      console.error("Error switching organization:", error);
      // Revert to previous state so role is never left in a downgraded limbo.
      setLocalActiveOrganization(previousOrg);
      setRole(previousRole);
      localStorage.setItem('activeOrganization', JSON.stringify(previousOrg));
    }
  };

  useEffect(() => {
    let isProcessing = false; // Flag para evitar múltiples ejecuciones simultáneas

    // 2026-10-21: Safari stopped being able to refresh the Firebase ID token
    // (securetoken.googleapis.com preflight 403) and every failed refresh sent
    // the person to the login — from a background tab, hours before the 8-hour
    // inactivity rule — although the CLU session key, which the backend
    // authenticates on by itself, still had most of its 24 hours left. While
    // that key lives, a failed Firebase refresh keeps the session as it is: no
    // redirect, no cleared role. Only the inactivity rule, a manual logout, a
    // blocked account / suspended org, or the backend refusing the key itself
    // (the 401/403 handling in api.service) ends it.
    const keepSessionOnTokenFailure = (
      firebaseUser: { uid?: string; email?: string | null },
      why: string,
    ): boolean => {
      // Firebase saying the account itself is gone is not a refresh failure.
      if (why === 'auth/user-disabled' || why === 'auth/user-not-found') return false;
      if (!UserSessionRegistry.hasValidStoredSessionKey(firebaseUser.uid)) return false;
      // The role kept comes from authData, which is not keyed by uid: keep it
      // only when it belongs to this very user.
      try {
        const stored = JSON.parse(localStorage.getItem('authData') || '{}');
        const storedEmail = String(stored?.email || '').trim().toLowerCase();
        const userEmail = String(firebaseUser.email || '').trim().toLowerCase();
        if (!storedEmail || !userEmail || storedEmail !== userEmail) return false;
      } catch {
        return false;
      }
      console.warn(`⚠️ [AuthContext] Firebase token refresh failed (${why}); the CLU session key is valid, keeping the session.`);
      recordAuthEvent('firebase-refresh-failed-session-kept', { reason: why, user: true, withStack: false });
      setLoading(false);
      isProcessing = false;
      return true;
    };

    // Loading guard: if Firebase never fires onAuthStateChanged within 10s, unblock the UI
    const loadingGuardTimer = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn('[AuthContext] Loading guard triggered — Firebase did not respond in 10s');
          return false;
        }
        return prev;
      });
    }, 10_000);

    // MEJORA: Manejar resultado de redirect login si existe
    const handleRedirectResult = async () => {
      try {
        const { getRedirectResult } = await import('firebase/auth');
        const result = await getRedirectResult(auth);
        if (result) {
          console.log("✅ [AuthContext] Redirect login exitoso, usuario autenticado:", result.user.email);
          clearRedirectSignInPending();
          // onAuthStateChanged se disparará automáticamente con el usuario
        } else {
          // Verificar si había un intento de redirect login
          if (isRedirectSignInPending()) {
            console.log("⚠️ [AuthContext] Redirect login cancelado o falló");
            clearRedirectSignInPending();
          }
        }
      } catch (redirectError: any) {
        console.error("❌ [AuthContext] Error manejando redirect login:", redirectError);
        clearRedirectSignInPending();
      }
    };

    // Ejecutar manejo de redirect al montar el componente. Se guarda la promesa
    // para que onAuthStateChanged pueda esperarla antes de tratar un usuario
    // nulo como "sesión cerrada".
    const redirectResolution = handleRedirectResult();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Un redirect sign-in en vuelo emite un `null` intermedio antes de que
      // getRedirectResult resuelva. Procesarlo limpiaría el estado y haría
      // parpadear el formulario de login sobre un login que sí va a completar.
      if (!user && isRedirectSignInPending()) {
        console.log("⏳ [AuthContext] Redirect sign-in en curso, ignorando estado nulo intermedio...");
        await redirectResolution;
        if (auth.currentUser) {
          // El redirect resolvió con usuario: onAuthStateChanged volverá a
          // dispararse con él. Este evento ya no aplica.
          return;
        }
        // El redirect falló o fue cancelado: seguir y procesar como no autenticado.
      }

      // Evitar procesamiento simultáneo
      if (isProcessing) {
        console.log("⚠️ Auth process already in progress, skipping...");
        return;
      }
      
      isProcessing = true;
      setLoading(true); // Mantener loading hasta que todo esté listo
      
      try {
        // Si no hay usuario, limpiar estado inmediatamente
        if (!user) {
          recordAuthEvent('firebase-user-null', { withStack: false });
          console.log("🔍 [AuthContext] Usuario no autenticado, limpiando estado...");
          React.startTransition(() => {
            setCurrentUser(null);
            setUserInfo(null);
            setRole(null);
            setUserOrganizations([]);
            setLocalActiveOrganization(null);
          });
          
          // Limpiar localStorage si no hay usuario
          localStorage.removeItem('authData');
          localStorage.removeItem('user');
          localStorage.removeItem('activeOrganization');
          localStorage.removeItem('userOrganizations');
          
          // Limpiar UserSessionRegistry
          try {
            UserSessionRegistry.clearCache();
          } catch (e) {
            console.warn('[AuthContext] Error limpiando UserSessionRegistry:', e);
          }
          
          setLoading(false);
          isProcessing = false;
          return;
        }
        
        setCurrentUser(user);

      if (user) {
          // Get the ID token (forzar refresh si es necesario)
          let idToken;
          try {
            idToken = await user.getIdToken(true);
          } catch (tokenError: any) {
            console.error("❌ [AuthContext] Error obteniendo token de Firebase:", tokenError);
            if (keepSessionOnTokenFailure(user, tokenError?.code || 'token-refresh-failed')) {
              return;
            }
            // FM-003: red caída — limpiar session key stale pero NO invalidar currentUser
            if (tokenError?.code === 'auth/network-request-failed') {
              console.warn("⚠️ [AuthContext] Red no disponible al refrescar token. Limpiando session key stale.");
              try { UserSessionRegistry.clearCache(); } catch { /* ignore */ }
              React.startTransition(() => {
                setUserInfo(null);
                setRole(null);
              });
              setLoading(false);
              isProcessing = false;
              return;
            }
            // Si el token está completamente expirado y no se puede refrescar,
            // la sesión de Firebase puede haber expirado - redirigir al login
            if (tokenError?.code === 'auth/user-token-expired' ||
                tokenError?.message?.includes('expired') ||
                tokenError?.message?.includes('Token expired')) {
              console.warn("⚠️ [AuthContext] Token completamente expirado, sesión puede haber expirado. Redirigiendo al login...");
              setLoading(false);
              isProcessing = false;
              // Limpiar estado y redirigir
              React.startTransition(() => {
                setCurrentUser(null);
                setUserInfo(null);
                setRole(null);
                setUserOrganizations([]);
                setLocalActiveOrganization(null);
              });
              broadcastLogoutEvent('session-expired');
              localStorage.removeItem('authData');
              localStorage.removeItem('user');
              localStorage.removeItem('activeOrganization');
              redirectToLogin('session-expired');
              return;
            }
            throw tokenError; // Re-lanzar si es otro tipo de error
          }
          
          // Skip auth gateway for public onboarding flow — user is not in DB yet.
          // RegisterStep will call /api/public/onboarding/register to create the user.
          //
          // skipsAuthGateway, NOT isPublicRoute: `/firebase-login` is public for
          // redirect purposes but is the one page that MUST call the gateway —
          // it is where the session is established. Using the wider predicate
          // here left every sign-in without a role and bounced it back to the
          // login screen. See utils/publicRoutes.js.
          if (skipsAuthGateway()) {
            console.log("🎯 [AuthContext] Onboarding route — skipping auth/gateway. The user is not in the DB yet.");
            setLoading(false);
            isProcessing = false;
            return;
          }

          console.log("🔐 Authenticating via Auth Gateway...");
          const { apiCall } = await import('../services/api.service');
          let userData: any = null;
          let retryCount = 0;
          // 1 retry (token-refresh on 401) is enough now that the backend caps
          // Firebase SDK calls with FIREBASE_HTTP_TIMEOUT_SECONDS — 3 attempts of
          // 20s each just amplified load while the backend was degraded.
          const maxRetries = 1;

          while (retryCount <= maxRetries) {
            try {
              userData = await apiCall('auth/gateway', 'POST', { id_token: idToken }, false, {
                disableAutoLogoutOn401: true,
                shortTimeout: 20000,
              });
              break;
            } catch (gatewayError: any) {
              const gatewayStatus = gatewayError?.status;
              const detail = String(gatewayError?.detail || gatewayError?.message || '');
              const accessStateCode = gatewayError?.rawDetail?.error;
              const isAccountBlocked = gatewayStatus === 403 && accessStateCode === 'account_blocked';
              const isOrgSuspended = gatewayStatus === 403 && accessStateCode === 'org_suspended';
              const isAccessDenied = gatewayStatus === 403 && detail.includes('ACCESS_DENIED');
              const isUnauthorized = gatewayStatus === 401;

              // account_blocked / org_suspended are NOT token problems — never enter the
              // 401 retry logic below, sign out immediately, and redirect with a
              // state-specific message. Checked before isAccessDenied/isUnauthorized
              // on purpose: these are structured 403s, not the legacy string-prefixed one.
              if (isAccountBlocked || isOrgSuspended) {
                const reason = isAccountBlocked ? 'account-blocked' : 'org-suspended';
                console.warn(`⛔ [AuthContext] ${accessStateCode} from auth gateway. Signing out and redirecting.`);
                setLoading(false);
                isProcessing = false;
                try { await auth.signOut(); } catch { /* ignore */ }
                React.startTransition(() => {
                  setCurrentUser(null);
                  setUserInfo(null);
                  setRole(null);
                  setUserOrganizations([]);
                  setLocalActiveOrganization(null);
                });
                broadcastLogoutEvent(reason);
                localStorage.removeItem('authData');
                localStorage.removeItem('user');
                localStorage.removeItem('activeOrganization');
                redirectToLogin(reason);
                return;
              }

              if (isAccessDenied) {
                console.warn('⛔ [AuthContext] Access denied by auth gateway. Redirecting to login.');
                setLoading(false);
                isProcessing = false;
                React.startTransition(() => {
                  setCurrentUser(null);
                  setUserInfo(null);
                  setRole(null);
                  setUserOrganizations([]);
                  setLocalActiveOrganization(null);
                });
                broadcastLogoutEvent('access-denied');
                localStorage.removeItem('authData');
                localStorage.removeItem('user');
                localStorage.removeItem('activeOrganization');
                redirectToLogin('access-denied');
                return;
              }

              if (isUnauthorized && retryCount < maxRetries) {
                console.warn(`🔍 [AuthContext] 401 from auth gateway, refreshing token and retrying (${retryCount + 1}/${maxRetries + 1})...`);
                try {
                  idToken = await user.getIdToken(true);
                  retryCount++;
                  await new Promise(resolve => setTimeout(resolve, 500));
                  continue;
                } catch (refreshError: any) {
                  if (keepSessionOnTokenFailure(user, refreshError?.code || 'gateway-retry-refresh-failed')) {
                    return;
                  }
                  const refreshMessage = String(refreshError?.message || '');
                  const refreshExpired = refreshError?.code === 'auth/user-token-expired'
                    || refreshMessage.includes('expired')
                    || refreshMessage.includes('Token expired');
                  if (refreshExpired) {
                    console.warn("⚠️ [AuthContext] Token expired during gateway retry. Redirecting to login.");
                    setLoading(false);
                    isProcessing = false;
                    React.startTransition(() => {
                      setCurrentUser(null);
                      setUserInfo(null);
                      setRole(null);
                      setUserOrganizations([]);
                      setLocalActiveOrganization(null);
                    });
                    broadcastLogoutEvent('session-expired');
                    localStorage.removeItem('authData');
                    localStorage.removeItem('user');
                    localStorage.removeItem('activeOrganization');
                    redirectToLogin('session-expired');
                    return;
                  }
                }
              }

              throw gatewayError;
            }
          }

          if (!userData) {
            throw new Error('No response received from authentication endpoint');
          }

          // Adopt the CLU session key issued inline by the gateway and strip it
          // from the payload BEFORE any logging — the key must never reach the
          // console. With this, no parallel /api/v1/session/exchange is needed.
          if (userData?.session?.key_value && user?.uid) {
            try {
              const { UserSessionRegistry } = await import('../stores/user-session.registry');
              UserSessionRegistry.adoptSessionKey(userData.session.key_value, userData.session.expires_at, user.uid);
            } catch (adoptError) {
              console.warn('⚠️ [AuthContext] Could not adopt inline session key, exchange fallback will handle it:', adoptError);
            }
          }
          delete userData.session;

          // access_state: "onboarding_incomplete" — 200, NOT an error. currentUser
          // stays alive (login was technically successful), but we must NOT continue
          // into the normal success flow below (which would eventually navigate to
          // /menu). Redirect straight to the wizard at its current step instead.
          // This replaces the old onboarding_completed/hasOrganization redirect that
          // used to live further down in this handler (§ access-state fix design).
          // Do not log userData.onboarding or the session_id anywhere — it is a
          // capability token for the unauthenticated public session-resume endpoint.
          if (userData?.access_state === 'onboarding_incomplete') {
            const onboardingSessionId = userData?.onboarding?.session_id;
            console.log('🎯 [AuthContext] onboarding_incomplete — resuming wizard.');
            setLoading(false);
            isProcessing = false;
            window.location.href = onboardingSessionId
              ? `/onboarding?session_id=${encodeURIComponent(onboardingSessionId)}`
              : '/onboarding';
            return;
          }

          // 🔍 LOG DETALLADO: Respuesta del backend (sin bloque session)
          console.log("🔍 [AuthContext] === RESPUESTA DEL BACKEND ===");
          console.log("🔍 [AuthContext] userData?.user:", userData?.user);
          console.log("🔍 [AuthContext] userData?.user?.role_code:", userData?.user?.role_code);
          console.log("🔍 [AuthContext] userData?.user?.role:", userData?.user?.role);
          console.log("🔍 [AuthContext] userData?.user?.organization_id:", userData?.user?.organization_id);
          console.log("🔍 [AuthContext] userData?.user?.organization_name:", userData?.user?.organization_name);
          try {
            localStorage.removeItem(LOGOUT_EVENT_KEY);
          } catch {
            // ignore
          }
          
          // Initialize agents from login response (optimization: avoid extra request)
          if (userData?.agents && Array.isArray(userData.agents) && userData.agents.length > 0) {
            try {
              const { AgentsRegistry } = await import('../stores/agents.registry');
              const userEmail = userData?.user?.email;
              const userRole = userData?.user?.role_code || userData?.user?.role;
              const userOrgId = userData?.user?.organization_id;
              
              AgentsRegistry.initializeFromLogin(userData.agents, userEmail, userOrgId, userRole);
              console.log(`✅ [AuthContext] Initialized ${userData.agents.length} agents from login response`);
            } catch (agentsError) {
              console.warn(`⚠️ [AuthContext] Error initializing agents from login: ${agentsError}`);
              // Don't fail login if agents initialization fails
            }
          }
          
          // Extraer datos del usuario desde la respuesta del gateway
          const backendRole = userData?.user?.role_code || userData?.user?.role || null;
          const backendOrgId = userData?.user?.organization_id || null;
          const organizationName = userData?.user?.organization_name || 'Default Organization';
              
          console.log("🔍 [AuthContext] === EXTRACCIÓN DE DATOS ===");
          console.log("🔍 [AuthContext] backendRole extraído:", backendRole);
          console.log("🔍 [AuthContext] backendOrgId extraído:", backendOrgId);
          console.log("🔍 [AuthContext] organizationName extraído:", organizationName);
          
          // Normalizar rol (ya debería venir correcto del gateway, pero por seguridad)
          const roleTransformationMap: { [key: string]: string } = {
            'Super Admin': 'super_user',
            'Admin': 'admin',
            'Member': 'user',
            'super_admin': 'super_user',
            'admin': 'admin',
            'member': 'user'
          };
          
          const localRoleCode = backendRole && ['super_user', 'admin', 'user'].includes(backendRole)
            ? backendRole
            : (backendRole ? (roleTransformationMap[backendRole] || backendRole || 'user') : 'user');
          
          console.log("🔍 [AuthContext] === NORMALIZACIÓN DE ROL ===");
          console.log("🔍 [AuthContext] backendRole original:", backendRole);
          console.log("🔍 [AuthContext] localRoleCode normalizado:", localRoleCode);
          console.log("🔍 [AuthContext] ¿Es super_user?:", localRoleCode === 'super_user');
              
          // Backend is the source of truth for role — always use what it returns.
          const roleCodeToUse = localRoleCode;
          
          // Invalidar cachés si el rol cambió
          const previousRole = role;
          if (previousRole && previousRole !== roleCodeToUse) {
            console.log(`[AuthContext] Rol cambiado: ${previousRole} -> ${roleCodeToUse}. Invalidando cachés...`);
            
            // Invalidar UserSessionRegistry
            try {
              const { UserSessionRegistry } = await import('../stores/user-session.registry');
              UserSessionRegistry.invalidateUserData();
            } catch (e) {
              console.warn('[AuthContext] Error invalidando UserSessionRegistry:', e);
            }
            
            // Invalidar DashboardStatsRegistry
            try {
              const { DashboardStatsRegistry } = await import('../stores/dashboard-stats.registry');
              DashboardStatsRegistry.invalidate();
            } catch (e) {
              console.warn('[AuthContext] Error invalidando DashboardStatsRegistry:', e);
            }
            
            // Disparar evento personalizado para notificar a otros componentes
            window.dispatchEvent(new CustomEvent('userRoleChanged', { 
              detail: { 
                previousRole, 
                newRole: roleCodeToUse 
              } 
            }));
          }
          
          // Preparar datos finales del usuario
          const finalUserData = {
            ...userData,
            user: {
              ...userData.user,
              role_code: roleCodeToUse,
              role: roleCodeToUse,
              organization_id: backendOrgId,
              organization_name: organizationName,
              modules: getDefaultModules(roleCodeToUse),
              // Normalizar formato para compatibilidad
              members: userData.user.members || [{
                role: roleCodeToUse,
                    organization: {
                  id: backendOrgId,
                  name: organizationName
                }
              }]
            }
          };
          
          // Preparar authData para localStorage
          const authData = {
            role_code: roleCodeToUse,
            organization_id: backendOrgId || 0,
            organization_name: organizationName,
            email: userData?.user?.email,
            user_id: userData?.user?.id,
            name: userData?.user?.name,
            token: idToken,
            last_updated: new Date().toISOString(),
            corrected_from_backend: true
          };
            
          // Preparar activeOrganization
          const activeOrg = {
            organization_id: backendOrgId || 0,
            organization_name: organizationName,
            role_code: roleCodeToUse
          };
          
          console.log("🔍 [AuthContext] === PREPARACIÓN PARA LOCALSTORAGE ===");
          // Never log authData.token (Firebase ID token) — redact it, same
          // pattern as the hasToken boolean check elsewhere in this file.
          console.log("🔍 [AuthContext] authData a guardar:", JSON.stringify({ ...authData, token: authData.token ? '[REDACTED]' : authData.token }, null, 2));
          console.log("🔍 [AuthContext] authData.role_code:", authData.role_code);
          console.log("🔍 [AuthContext] activeOrg a guardar:", JSON.stringify(activeOrg, null, 2));
          console.log("🔍 [AuthContext] activeOrg.role_code:", activeOrg.role_code);
          
          // ACTUALIZAR TODO EN UNA SOLA OPERACIÓN BATCH para evitar múltiples re-renders
          // Esto previene el efecto de "parpadeo"
          console.log(`🔍 [AuthContext] === ACTUALIZANDO ESTADO CON ROL ===`);
          console.log(`🔍 [AuthContext] roleCodeToUse final: ${roleCodeToUse}`);
          console.log(`🔍 [AuthContext] ¿Es diferente del backend? ${roleCodeToUse !== localRoleCode ? 'SÍ (protegido)' : 'NO'}`);
          console.log(`🔍 [AuthContext] role actual antes de actualizar: ${role}`);
          
          // If a super_user just logged in and there's a stale impersonation
          // session in localStorage, auto-end it so the super_user is never
          // trapped in a login loop by a leftover impersonation_token cookie.
          if (roleCodeToUse === 'super_user' && localStorage.getItem('impersonation_active') === 'true') {
            console.warn('[AuthContext] Super user login — auto-ending stale impersonation session');
            localStorage.removeItem('impersonation_active');
            localStorage.removeItem('impersonation_target_email');
            setIsImpersonating(false);
            setImpersonatedEmail(null);
            try {
              const { apiCall: _endCall } = await import('../services/api.service');
              await _endCall('users/end_impersonation', 'POST');
            } catch (e) {
              console.warn('[AuthContext] Auto-end impersonation (best-effort) failed:', e);
            }
          }

          // Actualizar estados directamente (sin startTransition para asegurar actualización inmediata)
          setUserInfo(finalUserData);
          setRole(roleCodeToUse);
          setLocalActiveOrganization(activeOrg);
          
          console.log(`✅ [AuthContext] Estado actualizado: role = ${roleCodeToUse}`);
          
          // Verificar que se actualizó correctamente después de un pequeño delay
          setTimeout(() => {
            console.log(`🔍 [AuthContext] Verificación post-actualización: role actual = ${role}`);
          }, 100);
          
          // 💾 Persistir en localStorage PRIMERO (solo para sobrevivir recargas, no fuente de verdad)
          console.log("🔍 [AuthContext] === GUARDANDO EN LOCALSTORAGE (persistencia) ===");
          localStorage.setItem('authData', JSON.stringify(authData));
          localStorage.setItem('user', JSON.stringify(finalUserData.user));
          localStorage.setItem('activeOrganization', JSON.stringify(activeOrg));
          
          // Verificar que se guardó correctamente
          const savedAuthData = localStorage.getItem('authData');
          const savedActiveOrg = localStorage.getItem('activeOrganization');
          console.log("🔍 [AuthContext] Verificación post-guardado:");
          // Never log the raw savedAuthData string — it contains the Firebase
          // ID token in clear text. Redact before logging.
          console.log("🔍 [AuthContext] authData guardado (token redactado):", savedAuthData
            ? JSON.stringify({ ...JSON.parse(savedAuthData), token: '[REDACTED]' })
            : savedAuthData);
          console.log("🔍 [AuthContext] activeOrganization guardado:", savedActiveOrg);
          if (savedAuthData) {
            try {
              const parsed = JSON.parse(savedAuthData);
              console.log("🔍 [AuthContext] authData parseado.role_code:", parsed.role_code);
            } catch (e) {
              console.error("🔍 [AuthContext] Error parseando authData guardado:", e);
            }
          }
          
          // 🔄 Sincronizar con UserSessionRegistry DESPUÉS de guardar en localStorage
          // MEJORA: Sincronizar ANTES de setLoading(false) para evitar condiciones de carrera
          // Esto asegura que UserSessionRegistry tenga los datos más recientes ANTES de que
          // los componentes intenten hacer requests
          try {
            // Primero invalidar cache para forzar recarga desde localStorage actualizado
            UserSessionRegistry.clearCache();
            console.log(`✅ [AuthContext] Cache de UserSessionRegistry invalidado`);
            
            // Luego sincronizar con los datos nuevos
            // Asegurar que pasamos todos los datos disponibles, incluso si algunos son null
            const syncData = {
              firebaseUser: user,
              userInfo: finalUserData,
              role: roleCodeToUse || finalUserData?.user?.role_code || finalUserData?.user?.role || null,
              organizationId: backendOrgId || finalUserData?.user?.organization_id || null,
              email: userData?.user?.email || finalUserData?.user?.email || user?.email || null,
            };
            
            console.log(`🔍 [AuthContext] Datos a sincronizar con UserSessionRegistry:`, {
              hasFirebaseUser: !!syncData.firebaseUser,
              hasUserInfo: !!syncData.userInfo,
              role: syncData.role || '[MISSING]',
              email: syncData.email || '[MISSING]',
              organizationId: syncData.organizationId || '[MISSING]',
            });
            
            // MEJORA: Sincronizar de forma síncrona primero (actualiza el estado inmediatamente)
            UserSessionRegistry.syncFromAuthContext(syncData);
            console.log(`✅ [AuthContext] Sincronizado con UserSessionRegistry (síncrono)`);
            
            // MEJORA: Esperar un momento para asegurar que el estado se propagó
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Pre-generar headers para asegurar que estén listos antes de que los componentes hagan solicitudes
            try {
              const headers = await UserSessionRegistry.getAuthHeaders({ orgId: backendOrgId || undefined });
              console.log(`✅ [AuthContext] Headers de autenticación pre-generados:`, {
                hasEmail: !!headers['X-User-Email'] && headers['X-User-Email'] !== '[MISSING]',
                hasRole: !!headers['X-User-Role'] && headers['X-User-Role'] !== '[MISSING]',
                hasToken: !!headers['Authorization'] && headers['Authorization'] !== '[MISSING]',
              });
            } catch (headerError) {
              console.warn('[AuthContext] Error pre-generando headers (no crítico):', headerError);
            }
          } catch (e) {
            console.error('[AuthContext] ❌ Error crítico sincronizando con UserSessionRegistry:', e);
            // No continuar si la sincronización falla críticamente
            // Esto previene que el usuario quede en un estado inconsistente
            throw e;
          }
          
          console.log(`✅ Login exitoso: ${userData?.user?.email}, rol: ${roleCodeToUse}${roleCodeToUse !== localRoleCode ? ` (protegido, backend tenía ${localRoleCode})` : ''}, org: ${backendOrgId}`);

          // IMPORTANTE: Establecer loading en false DESPUÉS de sincronizar con UserSessionRegistry
          // MEJORA: Asegurar que la sincronización esté completa antes de permitir que los componentes
          // hagan requests. Esto evita condiciones de carrera donde apiCall() se ejecuta antes de que
          // UserSessionRegistry tenga los datos sincronizados.
          console.log("🔍 [AuthContext] Estableciendo loading = false después de sincronizar UserSessionRegistry");
          setLoading(false);
          isProcessing = false;
          
          // NOTE: onboarding-incomplete handling used to live here, keyed off the
          // client-guessed onboarding_completed/hasOrganization flags with a 500ms
          // setTimeout redirect racing FirebaseLogin's own 300ms navigation timer.
          // The backend's resolve_user_access_state() is now the single source of
          // truth for this — see the `access_state === 'onboarding_incomplete'`
          // branch right after `delete userData.session;` above, which returns
          // early (before setLoading(false)) and never lets execution reach here.
          // Nothing left to do in this spot.

          // Cargar organizaciones del usuario (en background, sin bloquear navegación)
          if (userData?.user?.email) {
            loadUserOrganizations(userData.user.email).then(orgs => {
              if (orgs && orgs.length > 0) {
                setUserOrganizations(orgs);
                localStorage.setItem('userOrganizations', JSON.stringify(orgs));

                const savedActive = localStorage.getItem('activeOrganization');
                const parsedActive = savedActive ? JSON.parse(savedActive) : null;
                const stillValid = parsedActive && orgs.some(
                  (o: any) => o.organization_id === parsedActive.organization_id
                );

                if (!stillValid) {
                  const fallback = orgs.find((o: any) => o.is_primary) || orgs[0];
                  const fallbackOrg = {
                    organization_id: fallback.organization_id,
                    organization_name: fallback.organization_name,
                    role_code: fallback.role_code
                  };
                  setLocalActiveOrganization(fallbackOrg);
                  localStorage.setItem('activeOrganization', JSON.stringify(fallbackOrg));
                }
              }
            }).catch(err => {
              console.warn("Error loading organizations:", err);
            });
          }
          
          // Reload Sentry configuration after successful login (no bloquear)
          reloadSentryConfigAfterAuth().catch((sentryError) => {
            console.warn("Warning: Could not reload Sentry configuration:", sentryError);
          });
          
        } else {
          // Usuario no autenticado - limpiar todo en batch
          React.startTransition(() => {
            setUserInfo(null);
            setRole(null);
            setUserOrganizations([]);
            setLocalActiveOrganization(null);
          });
          
          // 🔄 Limpiar UserSessionRegistry también
          try {
            UserSessionRegistry.invalidateUserData();
            console.log(`✅ [AuthContext] UserSessionRegistry limpiado`);
          } catch (e) {
            console.warn('[AuthContext] Error limpiando UserSessionRegistry:', e);
          }
          
          // 💾 Limpiar localStorage
          localStorage.removeItem('user');
          localStorage.removeItem('authData');
          localStorage.removeItem('activeOrganization');
          localStorage.removeItem('userOrganizations');
          
          // Establecer loading en false cuando no hay usuario
          console.log("🔍 [AuthContext] Usuario no autenticado, estableciendo loading = false");
          setLoading(false);
          isProcessing = false;
        }
      } catch (error: any) {
        console.error("🔍 [AuthContext] === ERROR EN PROCESO DE AUTENTICACIÓN ===");
        console.error("🔍 [AuthContext] Error completo:", error);
        console.error("🔍 [AuthContext] Error message:", error?.message);
        console.error("🔍 [AuthContext] Error stack:", error?.stack);

        // Siempre limpiar el session key stale del registry en cualquier error de auth
        try { UserSessionRegistry.clearCache(); } catch { /* ignore */ }

        const isNetworkError = error?.code === 'auth/network-request-failed';

        if (isNetworkError) {
          // FM-003: error de red — no invalidar currentUser, solo limpiar userInfo y registry
          console.warn("⚠️ [AuthContext] Error de red en auth — manteniendo currentUser, limpiando session cache.");
          React.startTransition(() => {
            setUserInfo(null);
            setRole(null);
          });
        } else {
          // Cualquier otro error de auth — limpiar userInfo y role
          React.startTransition(() => {
            setUserInfo(null);
            setRole(null);
          });
        }

        // Establecer loading en false cuando hay error
        console.log("🔍 [AuthContext] Error en autenticación, estableciendo loading = false");
        setLoading(false);
        isProcessing = false;
      }
      // NOTA: No usar finally aquí porque loading se establece explícitamente
      // en cada rama (éxito, sin usuario, error) para mejor control del timing
    });

    return () => { unsubscribe(); clearTimeout(loadingGuardTimer); };
  }, []);

  // Auth failure bus: sign out and redirect when SSE or API signals unrecoverable auth failure
  useEffect(() => {
    const handleAuthFailure = (reason: string) => {
      recordAuthEvent('auth-failure-bus', () => ({ reason, user: !!auth.currentUser }));
      console.warn('[AuthContext] Auth failure event received:', reason);
      localStorage.removeItem('impersonation_active');
      localStorage.removeItem('impersonation_target_email');
      localStorage.removeItem('userInfo');
      auth.signOut().catch(() => {});
      redirectToLogin('session-expired');
    };
    onAuthFailure(handleAuthFailure);
    return () => offAuthFailure(handleAuthFailure);
  }, []);

  // 🔄 Sincronización entre ventanas: Escuchar cambios en localStorage de otras ventanas
  // Nota: localStorage aquí solo actúa como mecanismo de comunicación entre ventanas,
  // NO como fuente de verdad. La fuente de verdad es el estado de React en AuthContext.
  useEffect(() => {
    const applyCrossTabLogout = (reason: string) => {
      recordAuthEvent('cross-tab-logout', () => ({ reason, user: !!auth.currentUser, withStack: false }));
      React.startTransition(() => {
        setCurrentUser(null);
        setUserInfo(null);
        setRole(null);
        setUserOrganizations([]);
        setLocalActiveOrganization(null);
        setIsImpersonating(false);
        setImpersonatedEmail(null);
        setLoading(false);
      });
      localStorage.removeItem('impersonation_active');
      localStorage.removeItem('impersonation_target_email');
      localStorage.removeItem('userInfo');
      try { UserSessionRegistry.clearCache(); } catch { /* ignore */ }
      auth.signOut().catch(() => {});
      redirectToLogin(reason);
    };

    const getFreshLogoutReason = () => {
      try {
        const rawEvent = localStorage.getItem(LOGOUT_EVENT_KEY);
        if (!rawEvent) return null;
        const parsed = JSON.parse(rawEvent);
        const eventTs = Number(parsed?.ts || 0);
        const isFresh = eventTs > 0 && (Date.now() - eventTs) < 30000;
        if (isFresh && parsed?.reason) return String(parsed.reason);
      } catch {
        // ignore parse issues
      }
      return null;
    };

    const evaluateCrossTabState = () => {
      if (isPublicRoute()) return;
      const freshReason = getFreshLogoutReason();
      if (freshReason) {
        try { localStorage.removeItem(LOGOUT_EVENT_KEY); } catch { /* ignore */ }
        applyCrossTabLogout(freshReason);
        return;
      }

      const hasAuthData = !!localStorage.getItem('authData');
      const hasUser = !!localStorage.getItem('user');
      if (!hasAuthData && !hasUser && currentUser) {
        applyCrossTabLogout('session-expired');
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LOGOUT_EVENT_KEY && e.newValue) {
        if (!isPublicRoute()) {
          const reason = getFreshLogoutReason() || 'session-expired';
          try { localStorage.removeItem(LOGOUT_EVENT_KEY); } catch { /* ignore */ }
          applyCrossTabLogout(reason);
        }
        return;
      }

      // Cross-tab logout: when another tab removes authData, this tab must logout too
      if ((e.key === 'authData' || e.key === 'user') && e.newValue === null) {
        console.warn('[AuthContext] Auth data removed in another tab, logging out this tab');
        let reason = 'session-expired';
        try {
          const rawEvent = localStorage.getItem(LOGOUT_EVENT_KEY);
          if (rawEvent) {
            const parsed = JSON.parse(rawEvent);
            const eventTs = Number(parsed?.ts || 0);
            const isFresh = eventTs > 0 && (Date.now() - eventTs) < 30000;
            if (isFresh && parsed?.reason) {
              reason = parsed.reason;
            }
          }
        } catch {
          // Keep default reason
        }
        try {
          localStorage.removeItem(LOGOUT_EVENT_KEY);
        } catch {
          // ignore
        }
        applyCrossTabLogout(reason);
        return;
      }

      // Cross-tab auth sync: trigger a backend re-sync instead of trusting localStorage role.
      // Never elevate or modify in-memory role from a storage event — only the backend is authoritative.
      if (e.key === 'authData' && e.newValue) {
        try {
          const newAuthData = JSON.parse(e.newValue);
          const newEmail = newAuthData.email;
          const currentEmail = userInfo?.user?.email;
          if (currentEmail === newEmail) {
            console.log('[AuthContext] authData changed in another tab — triggering backend re-sync');
            // forceSyncWithBackend() no recarga orgs por su cuenta (ver su
            // comentario) — se recargan acá, explícito, una sola vez.
            forceSyncWithBackend().then((ok) => {
              if (ok && newEmail) {
                return loadUserOrganizations(newEmail).then((orgs) => {
                  if (orgs && orgs.length > 0) {
                    setUserOrganizations(orgs);
                    localStorage.setItem('userOrganizations', JSON.stringify(orgs));
                  }
                });
              }
            }).catch(() => {});
          }
        } catch (e) {
          console.warn('[AuthContext] Error procesando cambio de storage:', e);
        }
      }
      
      // Sincronizar activeOrganization también
      if (e.key === 'activeOrganization' && e.newValue) {
            try {
          const newActiveOrg = JSON.parse(e.newValue);
          const currentOrgId = activeOrganization?.organization_id;
          
          // Solo actualizar si es diferente
          if (newActiveOrg.organization_id !== currentOrgId) {
            console.log(`🔄 [AuthContext] Sincronizando organización activa desde otra ventana`);
            React.startTransition(() => {
              setLocalActiveOrganization(newActiveOrg);
            });
          }
        } catch (e) {
          console.warn('[AuthContext] Error procesando cambio de activeOrganization:', e);
        }
                  }
                };
                
    // Escuchar cambios de storage (de otras ventanas/tabs)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', evaluateCrossTabState);
    document.addEventListener('visibilitychange', evaluateCrossTabState);
    const crossTabGuardInterval = window.setInterval(evaluateCrossTabState, 4000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', evaluateCrossTabState);
      document.removeEventListener('visibilitychange', evaluateCrossTabState);
      window.clearInterval(crossTabGuardInterval);
    };
  }, [role, userInfo, activeOrganization, currentUser]);

  // Efecto para refresh proactivo de tokens y logout por inactividad
  useEffect(() => {
    if (!currentUser) return;

    refreshAuthJournalSwitch();
    console.log("🔄 Configurando refresh proactivo de token Firebase y detección de inactividad");
    
    let tokenRefreshInterval: NodeJS.Timeout | null = null;
    let inactivityCheckInterval: NodeJS.Timeout | null = null;
    let lastActivityTime = Date.now();
    let inactiveLogoutMins = 10; // Default policy if backend is unavailable
    let inactiveLogoutDisabled = false; // Flag para deshabilitar logout por inactividad
    
    // Obtener configuración de timeout desde una sola fuente de verdad backend.
    const fetchInactiveLogoutSetting = async () => {
      const policyCacheKey = 'auth:inactive_logout_policy';
      const applyPolicy = (policy: any) => {
        const parsedMins = Number(policy?.inactive_logout_mins);
        const safeMins = Number.isFinite(parsedMins) ? Math.min(1440, Math.max(1, parsedMins)) : 10;
        const enabled = policy?.inactive_logout_enabled !== false;
        inactiveLogoutMins = safeMins;
        inactiveLogoutDisabled = !enabled;
      };

      try {
        const { apiCall } = await import('../services/api.service');
        const data = await apiCall('auth/settings/inactive-logout', 'GET', null, false, {
          silentError: true,
          shortTimeout: 10000,
        });
        applyPolicy(data || {});
        localStorage.setItem(
          policyCacheKey,
          JSON.stringify({
            inactive_logout_enabled: !inactiveLogoutDisabled,
            inactive_logout_mins: inactiveLogoutMins,
            updated_at: new Date().toISOString(),
          })
        );

        if (inactiveLogoutDisabled) {
          console.log('⏱️ [AuthContext] Logout por inactividad DESHABILITADO');
        } else {
          console.log(`⏱️ [AuthContext] Logout por inactividad HABILITADO - ${inactiveLogoutMins} minutos`);
        }
      } catch (error) {
        console.warn("⏱️ [AuthContext] Could not refresh inactive logout policy; keeping last known value.", error);
      }
    };
    
    // Apply cached policy synchronously so the inactivity timer starts with the
    // last known config, then refresh from backend OUT of the login critical
    // path (this call used to race with auth/gateway and session/exchange).
    try {
      const cachedPolicy = localStorage.getItem('auth:inactive_logout_policy');
      if (cachedPolicy) {
        const policy = JSON.parse(cachedPolicy);
        const parsedMins = Number(policy?.inactive_logout_mins);
        inactiveLogoutMins = Number.isFinite(parsedMins) ? Math.min(1440, Math.max(1, parsedMins)) : 10;
        inactiveLogoutDisabled = policy?.inactive_logout_enabled === false;
      }
    } catch {
      // Ignore invalid cache
    }
    const inactivePolicyTimer = setTimeout(fetchInactiveLogoutSetting, 8000);
    
    // Detectar actividad del usuario (clicks, teclas, movimiento del mouse)
    const updateActivityTime = () => {
      lastActivityTime = Date.now();
    };
    
    // Eventos que indican actividad del usuario
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivityTime, { passive: true });
    });
    
    // Función para verificar inactividad y hacer logout si es necesario
    const checkInactivity = async () => {
      try {
        if (!currentUser) return;
        
        // Si el logout por inactividad está deshabilitado, no hacer nada
        if (inactiveLogoutDisabled) {
          return;
        }
        
        // Check if there are active processes that should prevent logout
        try {
          const { default: ActiveProcessesRegistry } = await import('../utils/activeProcessesRegistry');
          if (ActiveProcessesRegistry.hasActiveProcesses()) {
            const activeProcesses = ActiveProcessesRegistry.getActiveProcesses();
            console.log(`🔒 [AuthContext] Prevented logout: ${activeProcesses.length} active process(es) running:`, activeProcesses.map(p => `${p.type} (${p.processId})`));
            // Update activity time to prevent logout while processes are running
            updateActivityTime();
            return;
          }
        } catch (e) {
          console.warn('[AuthContext] Could not check active processes:', e);
        }
        
        const minutesSinceActivity = (Date.now() - lastActivityTime) / (1000 * 60);
        
        // Si el usuario está inactivo por más del tiempo configurado, hacer logout
        if (minutesSinceActivity >= inactiveLogoutMins) {
          console.warn(`🔒 Usuario inactivo por ${Math.round(minutesSinceActivity)} minutos. Cerrando sesión por seguridad...`);
          recordAuthEvent('inactivity-logout', {
            withStack: false,
            detail: { minutesSinceActivity: Math.round(minutesSinceActivity), limitMinutes: inactiveLogoutMins },
          });
          
          // Limpiar intervalos ANTES de hacer logout para evitar race conditions
          if (tokenRefreshInterval) {
            clearInterval(tokenRefreshInterval);
            tokenRefreshInterval = null;
          }
          if (inactivityCheckInterval) {
            clearInterval(inactivityCheckInterval);
            inactivityCheckInterval = null;
          }
          
          // Limpiar event listeners
          activityEvents.forEach(event => {
            window.removeEventListener(event, updateActivityTime);
          });
          
          // Limpiar estado local PRIMERO (antes de signOut para evitar race conditions)
          React.startTransition(() => {
            setCurrentUser(null);
        setUserInfo(null);
        setRole(null);
        setUserOrganizations([]);
        setLocalActiveOrganization(null);
            setLoading(false); // Asegurar que loading se resetea
          });
          
          // Limpiar localStorage ANTES de signOut
          broadcastLogoutEvent('auto-logout');
        localStorage.removeItem('authData');
          localStorage.removeItem('user');
        localStorage.removeItem('activeOrganization');
          localStorage.removeItem('userOrganizations');
          
          // Limpiar UserSessionRegistry y desactivar session key server-side
          UserSessionRegistry.logout(auth.currentUser?.uid || undefined).catch(() => {});

          // IMPORTANTE: Hacer logout de Firebase y ESPERAR a que complete
          // Esto previene problemas de CORS y estado inconsistente de Firebase
          try {
            await auth.signOut();
            console.log("✅ Firebase signOut completado correctamente");
          } catch (signOutError) {
            console.warn("Error en signOut de Firebase (continuando con redirección):", signOutError);
            // Aunque haya error, continuar con la limpieza
          }
          
          // Redirigir a login con hard reload COMPLETO (como cmd+r)
          // Esto evita los errores ERR_CONNECTION_REFUSED del HMR y problemas de CORS
          console.log("✅ Sesión cerrada por inactividad. Redirigiendo a login con reload completo...");
          
          // Usar window.location.reload() con replace para forzar reload completo
          // Esto funciona exactamente como cmd+r y limpia todo el estado
          setTimeout(() => {
            redirectToLogin('auto-logout', true);
          }, 300);
          
          return;
        }
      } catch (error: any) {
        console.error("Error verificando inactividad:", error);
      }
    };
    
    // Proactive token refresh: keep the token always fresh.
    // Firebase ID tokens expire after 60 minutes. We refresh every 10 minutes
    // to ensure the token always has >50 minutes of validity left.
    const refreshTokenProactively = async () => {
      try {
        if (!currentUser) return;

        // Skip refresh only if user will be logged out for inactivity anyway
        if (!inactiveLogoutDisabled) {
          const minutesSinceActivity = (Date.now() - lastActivityTime) / (1000 * 60);
          if (minutesSinceActivity >= inactiveLogoutMins) {
            return;
          }
        }

        // Check actual token expiry from JWT payload instead of relying on last_updated
        const savedAuthData = localStorage.getItem('authData');
        if (savedAuthData) {
          try {
            const authData = JSON.parse(savedAuthData);
            if (authData.token) {
              const payload = JSON.parse(atob(authData.token.split('.')[1]));
              const expiresAt = (payload.exp || 0) * 1000;
              const minutesUntilExpiry = (expiresAt - Date.now()) / (1000 * 60);
              // Only refresh if token expires in less than 10 minutes
              if (minutesUntilExpiry > 10) {
                return;
              }
              console.log(`[AuthContext] Token expires in ${Math.round(minutesUntilExpiry)} min, refreshing...`);
            }
          } catch {
            // Can't parse token, refresh to be safe
          }
        }

        // Force-refresh the token to get a new one valid for 60 minutes
        const freshToken = await currentUser.getIdToken(true);

        // Update localStorage
        try {
          const authData = JSON.parse(localStorage.getItem('authData') || '{}');
          if (authData.token !== freshToken) {
            authData.token = freshToken;
            authData.last_updated = new Date().toISOString();
            localStorage.setItem('authData', JSON.stringify(authData));
          }
        } catch (error) {
          console.warn('[AuthContext] Error updating token in localStorage:', error);
        }

        // Invalidate UserSessionRegistry headers cache so next call uses the fresh token
        try {
          UserSessionRegistry.clearCache();
        } catch { /* non-critical */ }
      } catch (error: any) {
        console.warn('[AuthContext] Proactive token refresh failed (non-critical):', error.message);
      }
    };

    // Refresh every 10 minutes (tokens last 60 min, so always >50 min of validity)
    tokenRefreshInterval = setInterval(refreshTokenProactively, 10 * 60 * 1000);
    
    // Verificar inactividad cada minuto (para detectar cuando se alcanza el tiempo configurado)
    inactivityCheckInterval = setInterval(checkInactivity, 60 * 1000);
    
    // Refrescar inmediatamente al montar si el token es viejo
    refreshTokenProactively();
    
    // Verificar inactividad inmediatamente al montar
    checkInactivity();
    
    // Cleanup
    return () => {
      clearTimeout(inactivePolicyTimer);
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
      if (inactivityCheckInterval) {
        clearInterval(inactivityCheckInterval);
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivityTime);
      });
    };
  }, [currentUser]);

  const [isImpersonating, setIsImpersonating] = useState<boolean>(() => {
    try { return localStorage.getItem('impersonation_active') === 'true'; } catch { return false; }
  });
  const [impersonatedEmail, setImpersonatedEmail] = useState<string | null>(() => {
    try { return localStorage.getItem('impersonation_target_email') || null; } catch { return null; }
  });

  const startImpersonation = async (targetEmail: string, reason: string = ''): Promise<boolean> => {
    try {
      // @ts-ignore
      const { apiCall } = await import('../services/api.service');

      // Step 1: set the impersonation_token cookie on the backend
      await apiCall(`users/${targetEmail}/impersonate`, 'POST', { reason });

      setIsImpersonating(true);
      setImpersonatedEmail(targetEmail);
      localStorage.setItem('impersonation_active', 'true');
      localStorage.setItem('impersonation_target_email', targetEmail);

      // Step 2: reload user context as the impersonated user.
      // The backend now returns impersonated-user data because the cookie is set.
      try {
        const impersonatedData = await apiCall('auth/me', 'GET');
        if (impersonatedData?.user) {
          const impUser = impersonatedData.user;
          const impRole = impUser.role_code || impUser.role || 'user';
          const impOrgId = impUser.organization_id || 0;
          const impOrgName = impUser.organization_name || '';

          // Pre-trigger module loading BEFORE React state switch to avoid race condition:
          // setRole('user') makes isSuperUser=false, but snap.loading is still stale-false
          // from the previous render. If we don't start loadModules here, ProtectedRoute
          // checks isModuleAuthorized with empty orgModules → redirects → blank page.
          // Starting loadModules synchronously sets state.loading=true + calls notify()
          // so setSnap({loading:true}) lands in the same React batch as setRole('user').
          try {
            const { ModulesRegistry } = await import('../stores/modules.registry');
            ModulesRegistry.loadModules(true, { email: impUser.email, orgId: impOrgId, role: impRole });
          } catch { /* non-critical */ }

          setUserInfo(impersonatedData);
          setRole(impRole);
          setLocalActiveOrganization({
            organization_id: impOrgId,
            organization_name: impOrgName,
            role_code: impRole,
          });
          localStorage.setItem('userInfo', JSON.stringify(impersonatedData));
          localStorage.setItem('user', JSON.stringify(impUser));
          localStorage.setItem('activeOrganization', JSON.stringify({
            organization_id: impOrgId,
            organization_name: impOrgName,
            role_code: impRole,
          }));

          // Reinitialize agents for the impersonated user
          if (impersonatedData.agents?.length > 0) {
            try {
              const { AgentsRegistry } = await import('../stores/agents.registry');
              AgentsRegistry.initializeFromLogin(impersonatedData.agents, impUser.email, impOrgId, impRole);
            } catch { /* non-critical */ }
          }

          // Clear all cached agent selections so every module starts fresh
          // with the impersonated user's agents (not the super_user's).
          localStorage.removeItem('clu-selected-agent');
          localStorage.removeItem('userOrganizations');
          try {
            const { UserSessionRegistry: _USR } = await import('../stores/user-session.registry');
            _USR.clearCache();
          } catch { /* non-critical */ }
          try {
            const { default: useCluStore } = await import('../features/clu/state/cluStore');
            (useCluStore as any).setState((s: any) => ({ ui: { ...s.ui, selectedAgentId: null } }));
          } catch { /* non-critical */ }
        }
      } catch (syncError) {
        console.warn('[Impersonation] Failed to reload user context — UI may still show super_user data:', syncError);
      }

      return true;
    } catch (error) {
      console.error('Impersonation error:', error);
      throw error;
    }
  };

  const endImpersonation = async (): Promise<boolean> => {
    // Clear all impersonation-related state — including keys startImpersonation wrote,
    // so the next page load doesn't initialize with stale impersonated-user data.
    localStorage.removeItem('impersonation_active');
    localStorage.removeItem('impersonation_target_email');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('user');
    localStorage.removeItem('activeOrganization');
    localStorage.removeItem('clu-selected-agent');
    localStorage.removeItem('userOrganizations');
    setIsImpersonating(false);
    setImpersonatedEmail(null);
    try {
      // @ts-ignore
      const { apiCall } = await import('../services/api.service');
      await apiCall('users/end_impersonation', 'POST');
    } catch (error) {
      console.warn('End impersonation API call failed — local state cleared, cookie may persist until next login:', error);
    }
    // Hard reload so onAuthStateChanged fires fresh and restores super_user state.
    window.location.href = '/';
    return true;
  };

  const value = {
    currentUser,
    login,
    signup,
    resetPassword,
    logout,
    signInWithGoogle,
    signInWithGoogleRedirect,
    cancelRedirectSignIn: clearRedirectSignInPending,
    loading,
    userInfo,
    role,
    userOrganizations,
    activeOrganization,
    setActiveOrganization: handleSetActiveOrganization,
    forceSyncWithBackend,
    startImpersonation,
    endImpersonation,
    isImpersonating,
    impersonatedEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
